import axios from 'axios';

const state = {
  reviews: [],
  currentReview: null,
  loading: false,
  error: null
};

const mutations = {
  SET_REVIEWS(state, reviews) {
    state.reviews = reviews;
  },
  SET_CURRENT_REVIEW(state, review) {
    state.currentReview = review;
  },
  ADD_REVIEW(state, review) {
    state.reviews.unshift(review);
  },
  UPDATE_REVIEW(state, updatedReview) {
    const index = state.reviews.findIndex(review => review.id === updatedReview.id);
    if (index !== -1) {
      state.reviews.splice(index, 1, updatedReview);
    }
  },
  DELETE_REVIEW(state, reviewId) {
    state.reviews = state.reviews.filter(review => review.id !== reviewId);
  },
  SET_LOADING(state, status) {
    state.loading = status;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  }
};

const actions = {
  async fetchReviews({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await axios.get('/admin/reviews');
      commit('SET_REVIEWS', response.data.reviews);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch reviews';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchReviewById({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await axios.get(`/admin/reviews/${id}`);
      commit('SET_CURRENT_REVIEW', response.data.review);
      return response.data.review;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch review';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createReview({ commit }, reviewData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const formData = new FormData();

      // Add all form fields
      Object.keys(reviewData).forEach(key => {
        if (reviewData[key] !== null && reviewData[key] !== undefined) {
          if (key === 'images' && Array.isArray(reviewData[key])) {
            // Send images as comma-separated URLs
            formData.append(key, reviewData[key].join(','));
          } else {
            formData.append(key, reviewData[key]);
          }
        }
      });

      const response = await axios.post('/admin/reviews', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      commit('ADD_REVIEW', response.data.review);
      return { success: true, review: response.data.review, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to create review';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateReview({ commit }, { id, reviewData }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const formData = new FormData();

      // Add all form fields
      Object.keys(reviewData).forEach(key => {
        if (key === 'images' && Array.isArray(reviewData[key])) {
          // Always send images field, even if empty (to allow removal)
          formData.append(key, reviewData[key].length > 0 ? reviewData[key].join(',') : '');
        } else if (key === 'video') {
          // Always send video field, even if null/empty (to allow removal)
          formData.append(key, reviewData[key] || '');
        } else if (reviewData[key] !== null && reviewData[key] !== undefined) {
          formData.append(key, reviewData[key]);
        }
      });

      // Ensure images field is always sent (even if empty) when editing
      if (!formData.has('images')) {
        formData.append('images', '');
      }

      // Ensure video field is always sent (even if null/empty) when editing
      if (!formData.has('video')) {
        formData.append('video', '');
      }

      const response = await axios.put(`/admin/reviews/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      commit('UPDATE_REVIEW', response.data.review);
      return { success: true, review: response.data.review, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update review';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteReview({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await axios.delete(`/admin/reviews/${id}`);
      commit('DELETE_REVIEW', id);
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete review';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  clearCurrentReview({ commit }) {
    commit('SET_CURRENT_REVIEW', null);
  }
};

const getters = {
  allReviews: state => state.reviews,
  currentReview: state => state.currentReview,
  isLoading: state => state.loading,
  error: state => state.error,
  reviewsCount: state => state.reviews.length
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
